import _ from 'lodash';
import {
  Fragment,
  startTransition,
  useCallback,
  useMemo,
  useState
} from 'react';

import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import { CompanyTableRow } from 'modules';
import { useInfiniteScroll } from 'modules/common/layer00/infiniteScrollUtil';
import * as types from '../../../../activities/company/CompaniesActivity/types';
import * as ds from './dataService';



export type CompanyTableProps = {
  formState: types.IFormState;
};

const tableHeaders = [
  'Symbol',
  'name',
  'price',
  'change',
  '% change',
  'volume',
  'avg vol',
  'market cap',
  'pe ratio (ttm)',
  '52 week range'
];

export const FC = (props: CompanyTableProps) => {
  const [
    isLoading,
    setIsLoading
  ] = useState(true);

  const input = __filterInputTrends(props);

  const {
    data,
    fetchMore,
    loading
  } = ds.useQuery({
    first: 20,
    offset: 0,
    freeTextSearch: props.formState.freeTextSearch,
    trends: input.length >= 1 ? input : undefined
  });

  const handleLoadMore = useCallback(
    () => {
      startTransition(() => {
        setIsLoading(() => true);
        fetchMore({
          variables: {
            options: {
              paginationInput: {
                limit: 20,
                offset: data.companyTableRows.length
              },
              filter: {
                timeFrame: 'oneYear',
                omniFreeText: props.formState.freeTextSearch,
                trends: input.map((node) => ({
                  name: node.name,
                  direction: node.direction
                })) ?? undefined
              }
            }
          },
          updateQuery: (prevQuery, { fetchMoreResult }) => {
            const companies = _.concat(...prevQuery.listedCompanies, fetchMoreResult.listedCompanies);

            return {
              ...prevQuery,
              listedCompanies: _.unionBy(companies, (node) => node.id)
            };
          }
        }).then(() => {
          return setIsLoading(() => false);
        });

      });

    }, [
      data.companyTableRows.length,
      fetchMore,
      input,
      props.formState.freeTextSearch
    ]
  );



  const observerTargetRef = useInfiniteScroll({
    onBecameVisible: () => {
      if (data.companyTableRows.length >= 20) {
        setIsLoading(() => true);
        handleLoadMore();
      }
    }
  });

  const loadingRows = useMemo(
    () => _.times(5, (i) => (
      <TableRow key={`${i}=loadingSkeleton`}>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
      </TableRow>
    )), []
  );

  const tableRows = useMemo(
    () => {
      const rows = data.companyTableRows.map((company, i) => {
        const arrLength = data.companyTableRows.length;
        if (i !== arrLength - 1) {
          return (
            <CompanyTableRow.FC
              {...company}
              key={`companyrow-${company.id}-${i}`}
            />
          );
        } else {
          return (
            <Fragment
              key={`targetDiv-${company.name}=frag`}
            >
              <div
                key={`targetDiv-${company.name}`}
                ref={observerTargetRef}
              />
              <CompanyTableRow.FC
                {...company}
                key={`companyrow-${company.id}-${company.change}-${i}`}
              />
            </Fragment>
          );
        }
      });



      setIsLoading(() => false);
      return rows;
    },
    [
      data.companyTableRows,
      observerTargetRef
    ]
  );

  return (
    <Box
      display='flex'
      alignContent='stretch'
      justifyContent='stretch'
    >
      <TableContainer
        sx={{
          paddingX: '1.5rem',
          height: '77vh',
          overflowY: 'scroll'
        }}
      >

        <Table
          stickyHeader
          sx={{
            backgroundColor: 'white'
          }}
        >
          <TableHead>


            <TableRow>
              {
                tableHeaders.map((header, i) => (
                  <TableCell
                    key={`${header}-${i}`}
                    variant='head'
                  >
                    {header}
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody >
            {/* <Suspense fallback = {<CircularProgress />}> */}
            {tableRows.length === 0 && !loading
              ? (
                <TableRow>
                  <TableCell>
                    <Typography
                      variant='tabSectionTextBold'
                    >
                      No results found
                    </Typography>
                  </TableCell>
                </TableRow>
              )
              : (
                tableRows
              )}
            {isLoading && (
              loadingRows
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>

  );
};


function __filterInputTrends(props: CompanyTableProps) {
  return props.formState.trends.filter((node) => node.name !== '' && node.direction !== '').map((node) => ({
    name: node.name,
    direction: node.direction
  }));
}

